import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, Navigate } from 'react-router-dom';
import config from '../../config.json';
import { getAdminSession } from '../../utils/adminSession';
import { getAdminAuthHeaders } from '../../utils/adminAuth';
import { endOfYearBackupUrl } from '../../utils/apiEndpoints';

const PageContainer = styled.div`
  max-width: 700px;
  margin: 4rem auto;
  padding: 2rem;
  background-color: var(--gray-900);
  color: var(--gray-50);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 1rem;
  text-align: center;
`;

const WarningCard = styled.div`
  margin: 1.5rem 0;
  padding: 1rem 1.25rem;
  border-radius: 10px;
  background: rgba(214, 85, 0, 0.12);
  border: 1px solid rgba(214, 85, 0, 0.5);
  color: #ffd8ae;
`;

const DetailText = styled.p`
  margin: 0;
  line-height: 1.6;
  color: var(--gray);
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-top: 1.25rem;
`;

const Label = styled.label`
  font-weight: 600;
`;

const Input = styled.input`
  width: 100%;
  max-width: 14rem;
  padding: 0.7rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #555;
  background: #111;
  color: #f9fafb;
  font: inherit;
`;

const HelperText = styled.span`
  font-size: 0.875rem;
  color: var(--gray);
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 1.5rem;
`;

const PrimaryButton = styled.button`
  padding: 0.8rem 1.25rem;
  border: none;
  border-radius: 8px;
  background: var(--primary);
  color: #fff;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(Link)`
  padding: 0.8rem 1.25rem;
  border-radius: 8px;
  border: 1px solid #555;
  background: transparent;
  color: var(--gray-50);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const WorkflowLink = styled.a`
  display: inline-block;
  margin-top: 0.75rem;
  color: inherit;
  font-weight: 600;
`;

const ResultBox = styled.div<{ $success: boolean }>`
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 8px;
  background: ${({ $success }) => ($success ? '#12331a' : '#3a1010')};
  color: ${({ $success }) => ($success ? '#c9f7d6' : '#ffd1d1')};
`;

const currentYear = String(config.stats?.currentYear || '26-27');
const seasonPattern = /^\d{2}-\d{2}$/;

const EndOfYearBackup: React.FC = () => {
  const [year, setYear] = useState(currentYear);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resultMessage, setResultMessage] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [workflowUrl, setWorkflowUrl] = useState<string | null>(null);
  const session = getAdminSession();
  const archiveFileName = `${year || 'YY-YY'}-stats.json`;
  const isYearValid = seasonPattern.test(year);

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  const handleBackup = async () => {
    if (!isYearValid) {
      setIsSuccess(false);
      setResultMessage('Enter the season in YY-YY format, for example 25-26.');
      return;
    }

    if (!window.confirm(
      `Start the end-of-year backup for ${year}? This will ask GitHub Actions to create a pull request for ${archiveFileName}.`
    )) {
      return;
    }

    setIsSubmitting(true);
    setResultMessage(null);
    setIsSuccess(false);
    setWorkflowUrl(null);

    try {
      const response = await fetch(endOfYearBackupUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAdminAuthHeaders(session),
        },
        body: JSON.stringify({ year }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Unable to start the backup workflow.');
      }
      setIsSuccess(true);
      setWorkflowUrl(result.workflowUrl || null);
      setResultMessage(
        `Backup workflow started for ${year}. A pull request will be created after the archive is generated.`
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to create the backup.';
      setIsSuccess(false);
      setResultMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <Title>End of Year Backup</Title>

      <WarningCard>
        Warning: this starts a GitHub Actions workflow to archive the current season and open a pull request.
        Run it only when the season is complete. Merging the archive pull request will automatically start a separate
        workflow that clears the live skater and goalie stats tables for the new season.
      </WarningCard>

      <DetailText>
        The workflow reads the live stats tables and creates <strong>{archiveFileName}</strong> under the old stats
        content folder. Review and merge the resulting pull request to publish the archive and start the stats reset.
      </DetailText>

      <FieldGroup>
        <Label htmlFor="backup-season">Season to archive</Label>
        <Input
          id="backup-season"
          name="year"
          type="text"
          value={year}
          onChange={(event) => setYear(event.target.value)}
          placeholder="25-26"
          pattern="[0-9]{2}-[0-9]{2}"
          maxLength={5}
          required
          aria-invalid={!isYearValid}
          aria-describedby="backup-season-help"
        />
        <HelperText id="backup-season-help">Use YY-YY format, for example 25-26.</HelperText>
      </FieldGroup>

      <ActionsRow>
        <PrimaryButton type="button" onClick={handleBackup} disabled={isSubmitting || !isYearValid}>
          {isSubmitting ? 'Starting workflow...' : 'Create backup and pull request'}
        </PrimaryButton>
        <SecondaryButton to="/admin">Back to admin</SecondaryButton>
      </ActionsRow>

      {resultMessage && (
        <ResultBox $success={isSuccess}>
          {resultMessage}
          {isSuccess && workflowUrl && (
            <div>
              <WorkflowLink href={workflowUrl} target="_blank" rel="noreferrer">
                View workflow runs
              </WorkflowLink>
            </div>
          )}
        </ResultBox>
      )}
    </PageContainer>
  );
};

export default EndOfYearBackup;
